import React, { MouseEvent } from 'react'

import { SxProps } from '@mui/material'

import { Color } from 'themes'
import { useIsMobile, useShortcutLabel } from 'utils/hooks'
import { useLocation } from 'react-router-dom'

import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import Divider from 'components_new/atoms/Divider'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import GroupAvatar from 'components_new/molecules/GroupAvatar'
import OverflowText from 'components_new/molecules/OverflowText'

import { matches } from './utils'

import { NAVBAR_COLLAPSED_TOOLTIP_OFFSET } from 'components_new/organisms/Navbar/lib'

export type TNavigationItem = {
  activePattern?: string | string[]
  activePaths?: string[]
  avatar?: {
    color: Color
    title: string
    variant?: 'group' | 'user' // 'circular' | 'rounded' ?
  }
  body?: string
  chip?: ChipProps
  divider?: boolean
  endIcon?: IconProps
  excludeActiveQueryParam?: string[]
  hidden?: boolean
  disabled?: boolean
  href?: string
  icon?: IconProps
  id?: string
  inactiveOnSearch?: boolean
  search?: string
  title: string
  titleIcon?: IconProps
  /** Dimmed parent shown before the title, as in a breadcrumb. */
  titlePrefix?: string
  neverActive?: boolean
  subItems?: TNavigationItem[]
  to?: string
  onClick?: () => void
}

export interface NavigationItemProps {
  active?: boolean
  arrow?: boolean
  border?: boolean
  className?: string
  color?: 'disabled' | 'primary' | 'secondary'
  expandedItems?: string[]
  external?: boolean
  disableHistory?: boolean
  item: TNavigationItem
  collapsed?: boolean
  indent?: number
  onToggle?: (id: string) => void
  shortCutKey?: string
  sx?: SxProps
}

const NavigationItem = (props: NavigationItemProps) => {
  const {
    active,
    arrow,
    border,
    className,
    color,
    expandedItems,
    external,
    disableHistory,
    item,
    collapsed,
    indent,
    onToggle,
    shortCutKey,
    sx
  } = props
  const isMobile = useIsMobile()
  const location = useLocation()

  const shortcut = useShortcutLabel(shortCutKey)

  if (item.hidden) {
    return null
  }

  const getActive = () => {
    if (item.inactiveOnSearch && location.search) {
      return false
    }

    if (
      item.excludeActiveQueryParam &&
      item.excludeActiveQueryParam.length > 0
    ) {
      const params = new URLSearchParams(location.search)

      if (item.excludeActiveQueryParam.some((key) => params.has(key))) {
        return false
      }
    }

    if (item.activePattern) {
      return matches(
        item.activePattern,
        `${location.pathname}${location.search}`
      )
    }

    return false
  }

  const selected = active || (getActive() && !item.neverActive)

  const getColor = (params?: { hover: boolean }) => {
    if (!selected && !params?.hover) return undefined

    if (item.icon?.color) {
      return `${item.icon.color}.main`
    } else {
      return 'brand.text'
    }
  }

  const expanded =
    expandedItems && item.id ? expandedItems.includes(item.id) : false

  const handleExpandMouseDown = (event: MouseEvent<HTMLElement>) => {
    if (item.subItems) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  const handleExpandClick = (event: MouseEvent<HTMLElement>) => {
    if (item.subItems) {
      event.stopPropagation()
      event.preventDefault()

      if (item.id && onToggle) {
        onToggle(item.id)
      }
    }
  }

  const getIndent = () => {
    if (indent !== undefined) {
      return indent + 1
    }

    return 1
  }

  const clickable = item.href || item.onClick || item.to

  return (
    <>
      {item.divider ? <Divider sx={{ my: 1 }} /> : null}

      <Tooltip
        slotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
        title={
          collapsed
            ? `${item.title}${shortcut ? ` (${shortcut.label})` : ''}`
            : undefined
        }
        placement={'right'}
      >
        <ListItemButton
          className={className}
          color="primary"
          disabled={item.disabled}
          disableHistory={disableHistory}
          disableRipple={!clickable}
          href={item.href}
          newTab={external}
          search={item.search}
          onClick={() => {
            if (item.onClick) {
              item.onClick()
            }
          }}
          selected={selected}
          sx={{
            borderRadius: 1,
            border: border ? '1px solid' : undefined,
            borderColor: border ? 'divider' : undefined,
            height: item.body ? undefined : 30,
            minHeight: item.body ? 44 : undefined,
            minWidth: 0,
            px: 1,
            py: isMobile ? undefined : 0.5,
            pl: indent ? indent * 4 : undefined,
            ...(border
              ? {
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default'
                }
              : null),
            ...(clickable
              ? {
                  '&:hover': {
                    '& .NavigationItemIcon': {
                      color: getColor({ hover: true })
                    },
                    '& .NavigationItemText': {
                      color: 'text.primary'
                    },
                    '& .NavigationTitleAction': {
                      opacity: 1
                    },
                    '& .NavigationTitleArrow': {
                      color: 'text.primary',
                      opacity: 1
                    },
                    '& .NavigationItemActionIcon': {
                      color: 'text.primary',
                      transform: 'translateX(2px)'
                    },
                    '& .NavigationItemShortcutSymbol': {
                      opacity: 1
                    }
                  }
                }
              : {
                  cursor: 'default',
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }),
            ...sx
          }}
        >
          {item.icon || item.avatar ? (
            <ListItemIcon
              onMouseDown={handleExpandMouseDown}
              onClick={handleExpandClick}
              sx={{
                position: 'relative',
                minWidth: 28,
                ...(item.subItems ? { cursor: 'default' } : null)
              }}
            >
              <>
                {item.subItems && item.subItems.length > 0 ? (
                  <Icon
                    name={
                      expanded ? 'ArrowDropDownOutlined' : 'ArrowRightOutlined'
                    }
                    sx={{
                      color: selected ? 'text.primary' : undefined,
                      cursor: 'default',
                      position: 'absolute',
                      top: 2,
                      left: -13,
                      fontSize: 16
                    }}
                  />
                ) : null}
                {item.icon ? (
                  <Icon
                    {...item.icon}
                    className="NavigationItemIcon"
                    color={undefined}
                    fontSize="small"
                    sx={{
                      color: getColor(),
                      ...(item.subItems ? { cursor: 'default' } : null)
                    }}
                  />
                ) : null}
                {item.avatar ? (
                  <GroupAvatar
                    color={item.avatar.color}
                    size="small"
                    title={item.avatar.title}
                  />
                ) : null}
              </>
            </ListItemIcon>
          ) : null}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.5s ease'
            }}
          >
            <Box
              sx={{
                color: selected
                  ? 'text.primary'
                  : `text.${color ?? 'secondary'}`,
                display: 'flex',
                flex: 1,
                minWidth: 0,
                mb: item.body ? 0.25 : undefined
              }}
            >
              {item.titlePrefix ? (
                <Text
                  color="text.disabled"
                  component="span"
                  noWrap={true}
                  sx={{ flexShrink: 0 }}
                  variant="navigation"
                >
                  {item.titlePrefix}
                  <Text
                    color="text.secondary"
                    component="span"
                    sx={{ mx: '0.25em' }}
                    variant="inherit"
                  >
                    /
                  </Text>
                </Text>
              ) : null}
              <OverflowText
                className="NavigationItemText"
                color="inherit"
                variant="navigation"
                sx={item.body ? { lineHeight: 1.1 } : undefined}
              >
                {item.title}
              </OverflowText>
              {item.titleIcon ? (
                <Icon
                  sx={{
                    color: 'text.secondary',
                    fontSize: '1em',
                    ml: '0.25em',
                    verticalAlign: 'middle'
                  }}
                  {...item.titleIcon}
                />
              ) : null}
            </Box>
            {item.body ? (
              <Text
                color={selected ? 'text.primary' : 'text.disabled'}
                component="div"
                lineHeight={1}
                variant="caption"
              >
                {item.body}
              </Text>
            ) : null}
          </Box>
          {item.chip ? (
            <Chip sx={{ ml: 1 }} size={'xsmall'} {...item.chip} />
          ) : null}
          {item.endIcon ? (
            <Icon sx={{ fontSize: '1em' }} {...item.endIcon} />
          ) : null}
          {shortcut ? (
            <Box
              className="NavigationItemShortcutSymbol"
              component="span"
              sx={{
                alignItems: 'center',
                color: 'text.disabled',
                display: 'inline-flex',
                gap: '0.1em',
                ml: 1,
                opacity: 0
              }}
            >
              {shortcut.label}
            </Box>
          ) : null}
          {arrow || external ? (
            <Icon
              className="NavigationItemActionIcon"
              name={external ? 'OpenInNew' : 'ArrowForwardIos'}
              sx={{
                color: 'text.disabled',
                fontSize: '1em',
                ml: 1,
                transform: 'translateX(0px)',
                transition: 'transform 0.2s ease'
              }}
            />
          ) : null}
        </ListItemButton>
      </Tooltip>

      {item.subItems && item.subItems?.length > 0 ? (
        <Collapse in={expanded}>
          {item.subItems?.map((subItem) => (
            <NavigationItem
              collapsed={false}
              expandedItems={expandedItems}
              indent={getIndent()}
              item={subItem}
              key={subItem.href}
              onToggle={onToggle}
            />
          ))}
        </Collapse>
      ) : null}
    </>
  )
}

NavigationItem.displayName = 'NavigationItem'
export default NavigationItem
